import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import '@livekit/components-styles';
import { Box,  Text, Center, Button, useToast, useBreakpointValue, Icon } from '@chakra-ui/react';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import io from 'socket.io-client';

import CustomMeetingLayout from './CustomMeetingLayout';

// Microphone icon component
const MicOffIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M19 11c0 1.19-.34 2.3-.9 3.28l-1.23-1.23c.27-.62.43-1.31.43-2.05H19zm-4 .16L9 5.18V5a3 3 0 0 1 6 0v6c0 .06 0 .11-.01.16zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
  </Icon>
);

export default function MeetingRoom() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState("");
  const [error, setError] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [socket, setSocket] = useState(null);
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // --- Live Attendance Tracking State ---
  const [totalTime, setTotalTime] = useState(0);
  const [presentTime, setPresentTime] = useState(0);
  const [absentTime, setAbsentTime] = useState(0);
  const [isPopupActive, setIsPopupActive] = useState(false);
  const hasSubmittedRef = useRef(false);
  
  // Timeline Tracking
  const currentStatusRef = useRef('present');
  const timelineRef = useRef([{ status: 'present', timestamp: Date.now() }]);

  // --- End Attendance State ---

  // Microphone permission state: 'checking' | 'granted' | 'prompt' | 'denied'
  const [micPermission, setMicPermission] = useState('checking');
  const [micRequestLoading, setMicRequestLoading] = useState(false);

  // Check microphone permission on mount
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        // Try the Permissions API first (Chrome/Edge)
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          setMicPermission(result.state); // 'granted', 'denied', or 'prompt'
          // Listen for changes
          result.onchange = () => {
            setMicPermission(result.state);
          };
          return;
        }
      } catch {
        // Permissions API not supported for microphone (Firefox/Safari)
      }

      // Fallback: try to enumerate devices to detect if permission was already granted
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMicLabel = devices.some(d => d.kind === 'audioinput' && d.label);
        setMicPermission(hasMicLabel ? 'granted' : 'prompt');
      } catch {
        setMicPermission('prompt');
      }
    };

    checkMicPermission();
  }, []);

  // Request microphone authorization
  const handleRequestMic = useCallback(async () => {
    setMicRequestLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop all tracks immediately — we only needed permission
      stream.getTracks().forEach(t => t.stop());
      setMicPermission('granted');
      toast({
        title: 'تم تفعيل الميكروفون',
        description: 'يمكنك الآن استخدام الميكروفون في الاجتماع.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Mic permission error:', err);
      // Keep as 'prompt' so the button stays visible for retry
      setMicPermission('prompt');
    } finally {
      setMicRequestLoading(false);
    }
  }, [toast]);

  const showMicPopup = micPermission === 'prompt' || micPermission === 'denied';

  const livekitUrl = process.env.REACT_APP_LIVEKIT_URL || "ws://localhost:7880";
  // Fallback to localhost if no env var, assuming standard local dev port 5000
  const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Get secretKey from navigation state (passed from MeetingsPage)
  const secretKey = location.state?.secretKey || "";

  useEffect(() => {
    if (!user) return;

    const fetchToken = async (keyToUse = secretKey) => {
      try {
        const response = await axiosInstance.post(`/meetings/token`, {
          meetingId: roomName,
          secretKey: keyToUse
        });
        if (response.data.success) {
          setToken(response.data.token);
        } else {
          setError(response.data.error || "Failed to get token");
        }
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.error || "خطأ في الانضمام إلى الحصة. تأكد من الرقم السري أو أن الحصة لا تزال نشطة.");
      }
    };

    const newSocket = io(backendUrl);
    setSocket(newSocket);

    if (user.role !== 'student') {
      // Instructors join the socket room to receive admission requests
      newSocket.emit("join_meeting_room", { roomName, user });
    }
    // All users fetch token immediately, bypassing the waiting room
    fetchToken();

    return () => {
      newSocket.disconnect();
    };
  }, [roomName, secretKey, user, backendUrl]);

  // --- Live Attendance Tracking Logic ---
  const [popupActiveDuration, setPopupActiveDuration] = useState(0);

  useEffect(() => {
    // Only track attendance for students, and only if they are in the meeting (have token)
    if (user?.role !== 'student' || !token) return;

    const timer = setInterval(() => {
      let isAbsent = false;

      setTotalTime(prev => {
        const nextTotal = prev + 1;
        // Trigger popup every 30 seconds
        if (nextTotal > 0 && nextTotal % 30 === 0) {
          setIsPopupActive(true);
          setPopupActiveDuration(0);
        }
        return nextTotal;
      });

      setIsPopupActive(prevPopup => {
        if (prevPopup) {
          setPopupActiveDuration(prevDuration => {
            const nextDuration = prevDuration + 1;
            // The absence time starts AFTER 5 seconds of the popup showing
            if (nextDuration > 5) {
              setAbsentTime(prev => prev + 1);
              isAbsent = true;
            } else {
              setPresentTime(prev => prev + 1);
            }
            
            // Handle Timeline
            const expectedStatus = isAbsent ? 'absent' : 'present';
            if (currentStatusRef.current !== expectedStatus) {
                timelineRef.current.push({ status: expectedStatus, timestamp: Date.now() });
                currentStatusRef.current = expectedStatus;
            }

            return nextDuration;
          });
        } else {
          setPresentTime(prev => prev + 1);
          // Handle Timeline
          if (currentStatusRef.current !== 'present') {
              timelineRef.current.push({ status: 'present', timestamp: Date.now() });
              currentStatusRef.current = 'present';
          }
        }
        return prevPopup; // maintain state
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [user, token]);

  const submitAttendance = useCallback(async () => {
    if (user?.role !== 'student' || hasSubmittedRef.current) return;
    hasSubmittedRef.current = true; // Prevent double submission
    
    // Add final timeline event indicating the end of the meeting/presence
    timelineRef.current.push({ status: 'end', timestamp: Date.now() });

    try {
      await axiosInstance.post('/live-attendance/submit', {
        meetingId: roomName,
        totalDuration: totalTime,
        presentDuration: presentTime,
        absentDuration: absentTime,
        timeline: timelineRef.current
      });
      console.log("Live attendance submitted.");
    } catch (err) {
      console.error("Failed to submit live attendance", err);
    }
  }, [user, roomName, totalTime, presentTime, absentTime]);

  const handleIAmPresentClick = () => {
    setIsPopupActive(false);
    setPopupActiveDuration(0);
  };
  // --- End Attendance Logic ---

  if (error) {
    return (
      <Center h="100vh" flexDirection="column" bg="gray.900" color="white">
        <Text fontSize="xl" color="red.400" mb={4}>{error}</Text>
        <Button colorScheme="blue" onClick={() => navigate(-1)}>العودة</Button>
      </Center>
    );
  }

  if (isWaiting) {
    return (
      <Center h="100vh" flexDirection="column" bg="gray.900" color="white">
        <CobraSpinner size="xl" color="orange.400" mb={4} />
        <Text fontSize="xl">أنت في غرفة الانتظار...</Text>
        <Text fontSize="md" color="gray.600">يرجى الانتظار حتى يسمح لك المعلم بالدخول</Text>
      </Center>
    );
  }

  if (!token) {
    return (
      <Center h="100vh" bg="gray.900" color="white">
        <CobraSpinner size="xl" color="teal.400" />
        <Text ml={4}>جاري الانضمام...</Text>
      </Center>
    );
  }

  return (
    <Box position="fixed" top={0} left={0} bottom={0} right={isMobile ? 0 : "70px"} overflow="hidden" bg="white" zIndex={10}>
      <style>{`
        .my-custom-livekit-room {
          /* Light Mode Colors */
          --lk-bg: #ffffff;
          --lk-control-bar-bg: #f7f9fa;
          --lk-accent: #0b5cff;
          --lk-accent-fg: #ffffff;
          --lk-danger: #ff2a2a;
          --lk-font-family: 'Tajawal', sans-serif;
          --lk-border-radius: 8px;
        }

        /* Control Bar Light Zoom Look */
        .lk-control-bar {
          background-color: #f7f9fa !important;
          border-top: 1px solid #e2e8f0 !important;
          padding: 8px 16px !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          gap: 12px !important;
          height: auto !important;
          direction: ltr !important; /* Force LTR for exact Zoom order */
          position: relative !important;
          width: 100% !important;
        }

        /* Group Buttons into columns (Icon on top, Text below) */
        .lk-control-bar .lk-button:not(.lk-button-menu) {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          background-color: transparent !important;
          border: none !important;
          border-radius: 6px !important;
          padding: 6px 8px !important;
          min-width: 65px !important;
          height: auto !important;
          color: #333333 !important; /* Light mode text */
          gap: 4px !important;
          transition: background-color 0.2s ease !important;
        }
        
        .lk-control-bar .lk-button:not(.lk-button-menu):hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
        }

        .lk-control-bar .lk-button svg {
          width: 20px !important;
          height: 20px !important;
        }

        /* Fix Caret Menu Buttons */
        .lk-control-bar .lk-button-menu {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 24px !important;
          min-width: 24px !important;
          padding: 0 !important;
          background: transparent !important;
          border: none !important;
          color: #333333 !important;
          height: 100% !important;
        }
        .lk-control-bar .lk-button-menu:hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
          border-radius: 4px !important;
        }

        /* Button Groups (Keep Mic+Caret and Camera+Caret together) */
        .lk-control-bar .lk-button-group {
          display: flex !important;
          align-items: stretch !important;
          background-color: transparent !important;
          border-radius: 6px !important;
          gap: 2px !important;
        }
        .lk-control-bar .lk-button-group:hover {
          background-color: rgba(0, 0, 0, 0.02) !important;
        }

        /* Position Left Controls (Mic Group, Camera Group) */
        @media (min-width: 768px) {
          .lk-control-bar > .lk-button-group:nth-of-type(1) {
            position: absolute !important;
            left: 16px !important;
          }
          .lk-control-bar > .lk-button-group:nth-of-type(2) {
            position: absolute !important;
            left: 110px !important; /* Adjusted for group width */
          }
          /* Position Right Control (End) */
          .lk-disconnect-button {
            position: absolute !important;
            right: 16px !important;
          }
        }

        @media (max-width: 767px) {
          .lk-control-bar {
            justify-content: space-evenly !important;
            gap: 4px !important;
            padding: 8px 4px !important;
          }
          .lk-control-bar > .lk-button-group {
            position: static !important;
          }
        }

        /* Hide default text nodes by setting font size to 0 on the button */
        .lk-control-bar .lk-button:not(.lk-button-menu) {
          font-size: 0 !important;
          color: transparent !important;
        }

        /* Inject Custom Zoom Texts */
        .lk-control-bar .lk-button:not(.lk-button-menu)::after {
          font-size: 11px !important;
          font-weight: 500 !important;
          color: #333333 !important; /* Light mode text */
          display: block;
          margin-top: 4px;
        }

        /* Camera / Video Texts */
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="true"]::after,
        .lk-control-bar .lk-button[data-lk-source="camera"].lk-button-active::after {
          content: "Stop Video";
        }
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="false"]::after,
        .lk-control-bar .lk-button[data-lk-source="camera"]:not([aria-pressed="true"]):not(.lk-button-active)::after {
          content: "Start Video";
        }

        /* Microphone / Audio Texts */
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="true"]::after,
        .lk-control-bar .lk-button[data-lk-source="microphone"].lk-button-active::after {
          content: "Mute";
        }
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="false"]::after,
        .lk-control-bar .lk-button[data-lk-source="microphone"]:not([aria-pressed="true"]):not(.lk-button-active)::after {
          content: "Unmute";
        }

        /* Screen Share Text */
        .lk-control-bar .lk-button[data-lk-source="screen_share"]::after {
          content: "Share Screen";
        }
        /* Screen Share Icon Zoom color (Green) */
        .lk-control-bar .lk-button[data-lk-source="screen_share"] svg {
          fill: #23d959 !important;
          color: #23d959 !important;
        }

        /* Chat Text */
        .lk-chat-toggle::after {
          content: "Chat";
        }

        /* Chat Icon */
        .lk-chat-toggle svg {
          fill: #0b5cff !important; /* Blue color */
          color: #0b5cff !important;
        }

        /* Leave Button Styling (End) */
        .lk-control-bar .lk-disconnect-button {
          background-color: #ff2a2a !important;
          color: white !important;
          margin-left: 0 !important;
          padding: 8px 16px !important;
          border-radius: 6px !important;
          font-size: 0 !important; /* hide default */
          font-weight: bold !important;
          flex-direction: row !important;
          min-width: 60px !important;
        }
        .lk-control-bar .lk-disconnect-button svg {
          display: none !important; /* Hide icon for End button to look like Zoom */
        }
        .lk-control-bar .lk-disconnect-button::after {
          content: "End";
          font-size: 13px !important;
          margin-top: 0 !important;
          color: white !important;
        }
        .lk-control-bar .lk-disconnect-button:hover {
          background-color: #d12222 !important;
        }

        /* Microphone & Camera States */
        /* Active (ON) - Dark Icon for Light Mode */
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="true"] svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="true"] svg,
        .lk-control-bar .lk-button[data-lk-source="camera"].lk-button-active svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"].lk-button-active svg {
          fill: #333333 !important;
          color: #333333 !important;
        }
        
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="true"] svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="true"] svg path,
        .lk-control-bar .lk-button[data-lk-source="camera"].lk-button-active svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"].lk-button-active svg path {
          fill: #333333 !important;
        }

        /* Inactive (OFF) - Red Icon */
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="false"] svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="false"] svg,
        .lk-control-bar .lk-button[data-lk-source="camera"]:not([aria-pressed="true"]):not(.lk-button-active) svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"]:not([aria-pressed="true"]):not(.lk-button-active) svg {
          fill: #ff2a2a !important;
          color: #ff2a2a !important;
        }

        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="false"] svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="false"] svg path,
        .lk-control-bar .lk-button[data-lk-source="camera"]:not([aria-pressed="true"]):not(.lk-button-active) svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"]:not([aria-pressed="true"]):not(.lk-button-active) svg path {
          fill: #ff2a2a !important;
        }

        /* Screen Share Active State */
        .lk-control-bar .lk-button[data-lk-source="screen_share"][aria-pressed="true"] svg,
        .lk-control-bar .lk-button[data-lk-source="screen_share"].lk-button-active svg {
          fill: #0b5cff !important;
          color: #0b5cff !important;
        }

        /* Chat Panel Styling for Light Mode */
        .lk-chat {
          border-left: 1px solid #e2e8f0 !important;
          background-color: #ffffff !important;
          color: #333333 !important;
        }
        
        .lk-chat-header {
          border-bottom: 1px solid #e2e8f0 !important;
          color: #1a202c !important;
          background-color: #f7f9fa !important;
          font-family: 'Tajawal', sans-serif;
          font-weight: bold !important;
        }

        .lk-chat-messages {
          background-color: #ffffff !important;
          position: relative !important;
        }
        
        .lk-chat-messages::before {
          content: "" !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background-image: url('/assets/cobra.png') !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          background-size: contain !important;
          opacity: 0.1 !important;
          z-index: 0 !important;
          pointer-events: none !important;
        }
        
        /* Fix Chat Messages Text */
        .lk-chat-entry {
          color: #ffffff !important;
          position: relative !important;
          z-index: 1 !important;
        }
        .lk-chat-entry .lk-message-body {
          color: #ffffff !important;
          background-color: #0b5cff !important; /* Blue bubble so white text is readable */
          padding: 8px 12px !important;
          border-radius: 8px !important;
          display: inline-block !important;
        }
        .lk-chat-entry .lk-meta-data {
          color: #888888 !important;
        }
        .lk-chat-entry .lk-participant-name {
          color: #333333 !important;
          font-weight: 600 !important;
        }
        
        .lk-chat-form {
          border-top: 1px solid #e2e8f0 !important;
          background-color: #f7f9fa !important;
        }
        
        .lk-chat-form input {
          color: #333333 !important;
          background-color: #ffffff !important;
          border: 1px solid #cbd5e0 !important;
        }

        /* Fix Video Backgrounds for Light Mode */
        .lk-video-conference, .lk-audio-conference, .lk-focus-layout {
          background-color: #0f172a !important;
        }

        /* Full Screen LiveKit Container */
        .lk-video-conference {
          width: 100% !important;
          height: 100% !important;
          position: relative !important;
          display: flex !important;
          flex-direction: column !important;
        }

        .lk-grid-layout {
          width: 100% !important;
          height: calc(100% - 70px) !important;
          padding: 16px !important;
          gap: 16px !important;
        }

        .lk-focus-layout-wrapper {
          width: 100% !important;
          height: calc(100% - 70px) !important;
          padding: 12px !important;
        }

        /* Sleek Participant Video Cards */
        .lk-participant-tile {
          background-color: #0f172a !important;
          border: 1.5px solid rgba(255, 255, 255, 0.12) !important;
          border-radius: 16px !important;
          box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.4) !important;
          overflow: hidden !important;
          position: relative !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }

        .lk-participant-tile:hover {
          border-color: rgba(59, 130, 246, 0.6) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 14px 35px -5px rgba(0, 0, 0, 0.5) !important;
        }

        .lk-participant-tile[data-lk-speaking="true"] {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4), 0 12px 32px -5px rgba(0, 0, 0, 0.5) !important;
        }

        .lk-participant-tile video {
          border-radius: 16px !important;
          object-fit: cover !important;
        }

        /* Fix Participant Name Tag overlay */
        .lk-participant-metadata {
          background-color: rgba(15, 23, 42, 0.85) !important;
          backdrop-filter: blur(12px) !important;
          color: #ffffff !important;
          border-radius: 10px !important;
          padding: 6px 12px !important;
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
        }

        .lk-participant-metadata .lk-participant-name {
          color: #ffffff !important;
          font-weight: 600 !important;
        }

        .lk-participant-metadata svg {
          fill: #ffffff !important;
        }

        /* Fix Indicator/Metadata Backgrounds */
        .lk-connection-quality, .lk-audio-visualizer {
          background-color: rgba(15, 23, 42, 0.85) !important;
          backdrop-filter: blur(8px) !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          border-radius: 8px !important;
        }
      `}</style>
      <LiveKitRoom
        video={true}
        audio={true}
        token={token}
        serverUrl={livekitUrl}
        data-lk-theme="default"
        className="my-custom-livekit-room"
        onDisconnected={() => {
          submitAttendance();
          navigate(-1);
        }}
        style={{ height: '100%', width: '100%' }}
      >
        <RoomAudioRenderer />
        {socket && <CustomMeetingLayout socket={socket} roomName={roomName} secretKey={secretKey} user={user} />}
      </LiveKitRoom>

      {/* --- Live Attendance Popup Overlay --- */}
      {isPopupActive && (
        <Box 
          position="fixed" 
          top="0" left="0" right="0" bottom="0" 
          bg="rgba(0,0,0,0.8)" 
          zIndex={9999} 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
        >
          <Box bg="white" p={8} borderRadius="xl" textAlign="center" boxShadow="2xl">
            <Text fontSize="2xl" fontWeight="bold" color="red.500" mb={4}>
              تأكيد الحضور
            </Text>
            <Text fontSize="lg" mb={6} color="gray.700">
              يرجى النقر على الزر أدناه لتأكيد تواجدك في الحصة.
            </Text>
            <Button colorScheme="green" size="lg" onClick={handleIAmPresentClick} px={12}>
              أنا حاضر
            </Button>
          </Box>
        </Box>
      )}

      {/* Microphone Authorization Banner */}
      {showMicPopup && (
        <Box
          position="absolute"
          top="16px"
          left="50%"
          transform="translateX(-50%)"
          zIndex={100}
          bg="rgba(17, 20, 24, 0.95)"
          border="1px solid"
          borderColor="orange.400"
          borderRadius="14px"
          boxShadow="0 4px 30px rgba(255, 184, 0, 0.2)"
          px={5}
          py={3}
          display="flex"
          alignItems="center"
          gap={3}
          dir="rtl"
          backdropFilter="blur(10px)"
          maxW="90vw"
        >
          <MicOffIcon boxSize={6} color="orange.400" />
          <Text color="white" fontSize="sm" fontFamily="'Tajawal', sans-serif" whiteSpace="nowrap">
            الميكروفون غير مفعّل
          </Text>
          <Button
            size="sm"
            onClick={handleRequestMic}
            isLoading={micRequestLoading}
            loadingText="..."
            bg="orange.400"
            color="black"
            fontWeight="bold"
            fontFamily="'Tajawal', sans-serif"
            borderRadius="10px"
            px={4}
            _hover={{ bg: 'orange.300', boxShadow: '0 2px 12px rgba(255, 184, 0, 0.3)' }}
            _active={{ bg: 'orange.500' }}
            transition="all 0.2s ease"
          >
            تفعيل الميكروفون
          </Button>
        </Box>
      )}
    </Box>
  );
}

